import manifest from './assetManifest.json';
import type {BirdScore} from './detections';
import {BIRD_INFERENCE_TIMEOUT_MS,validBirdScores} from './inferenceHealth';
export const birdModelBytes=manifest.files.reduce((n,f)=>n+f.bytes,0);
export interface BirdModel {predict(audio:Float32Array):Promise<BirdScore[]>;close():void}
export async function loadBirdModel(signal:AbortSignal,progress:(value:number)=>void,region?:{latitude:number;longitude:number}):Promise<BirdModel>{
 const files:{name:string;bytes:ArrayBuffer}[]=[];
 let cache:Cache|undefined;try{cache=await caches.open(manifest.cache);}catch{/* Storage may be unavailable; this session still works online. */}
 let loaded=0;
 for(const entry of manifest.files){
  signal.throwIfAborted();
  const url=new URL(manifest.base+entry.name,location.origin).href;
  let response=await cache?.match(url),fresh=false;
  if(!response){
   try{response=await fetch(url,{signal,credentials:'same-origin'});fresh=true;}
   catch(error){signal.throwIfAborted();throw Error(navigator.onLine===false?'Bird identification needs a download on this device. Connect to the internet once, then listening will work offline.':'Bird identification could not download. Check your connection and try again.');}
  }
  if(!response.ok)throw Error('Bird identification could not download. Check your connection and try again.');
  const bytes=await response.arrayBuffer();signal.throwIfAborted();
  const hash=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',bytes)),b=>b.toString(16).padStart(2,'0')).join('');
  if(bytes.byteLength!==entry.bytes||hash!==entry.sha256){await cache?.delete(url);throw Error('The bird model download was incomplete. Try again.');}
  if(fresh)try{await cache?.put(url,new Response(bytes.slice(0)));}catch{/* Quota does not prevent this session. */}
  files.push({name:manifest.base+entry.name,bytes});loaded+=entry.bytes;progress(Math.round(loaded/birdModelBytes*85));
 }
 const text=(name:string)=>new TextDecoder().decode(files.find(f=>f.name===manifest.base+name)!.bytes);
 const source=text('birdnet-worker.js');
 const expectedScores=text('models/birdnet/labels/en_us.txt').split('\n').length;
 if(!source.includes('importScripts(TF_PATH);')||!source.includes('init();'))throw Error('Bird identification version is incompatible.');
 const adapted=source.replace('importScripts(TF_PATH);','// TensorFlow loaded locally above.')
  .replace("const ROOT = params.get('root') || 'models';",`const ROOT = ${JSON.stringify(location.origin+manifest.base+'models')};`)
  .replace('init();','// Boot only after the verified local model bytes arrive.')
  .replace('const navigatorLang = params.get(\'lang\');',"const navigatorLang = 'en_us';");
 // Model fetches are resolved solely from the verified bytes transferred below.
 // No recording, prediction or location is sent over the network.
 const bridge=`
 const sonataFiles = new Map();
 self.fetch=async input=>{const key=new URL(typeof input==='string'?input:input.url,${JSON.stringify(location.origin)}).pathname;const value=sonataFiles.get(key);if(!value)throw Error('Unavailable model asset');return new Response(value.slice(0),{headers:{'Content-Type':key.endsWith('.json')?'application/json':'application/octet-stream'}});};
 self.addEventListener('unhandledrejection',()=>self.postMessage({message:'failure'}));
 `;
 const boot=`
 const sonataHandler=self.onmessage;
 self.onmessage=async event=>{try{if(event.data.message==='boot'){for(const f of event.data.files)sonataFiles.set(f.name,f.bytes);await init();}else await sonataHandler(event);}catch(error){self.postMessage({message:'failure'});}};
 `;
 signal.throwIfAborted();
 const url=URL.createObjectURL(new Blob([text('tf-4.22.0.min.js').replace(/\/\/# sourceMappingURL=.*$/gm,''),'\n',bridge,adapted,boot],{type:'text/javascript'}));
 const worker=new Worker(url);let closed=false,pending:((v:BirdScore[])=>void)|undefined,rejectPending:((e:Error)=>void)|undefined;
 let timeout:ReturnType<typeof setTimeout>|undefined;
 let failBoot:(e:Error)=>void=()=>{};
 const close=()=>{if(closed)return;closed=true;clearTimeout(timeout);worker.terminate();URL.revokeObjectURL(url);signal.removeEventListener('abort',close);const error=new Error('Bird identification stopped.');rejectPending?.(error);pending=undefined;rejectPending=undefined;failBoot(error);};
 signal.addEventListener('abort',close,{once:true});
 try{
  await new Promise<void>((resolve,reject)=>{
   failBoot=reject;timeout=setTimeout(()=>reject(Error('Bird identification is taking too long to start. Try again.')),90000);
   worker.onerror=()=>{const e=Error('Bird identification could not run on this device. Try again with an updated browser.');rejectPending?.(e);pending=undefined;rejectPending=undefined;reject(e);};
   worker.onmessage=({data})=>{
    if(closed)return;
    if(data.message==='failure'){const e=Error('Bird identification stopped unexpectedly. Try again.');rejectPending?.(e);pending=undefined;rejectPending=undefined;reject(e);}
    if(data.message==='warmup')progress(90);
    if(data.message==='loaded'){
     if(region)worker.postMessage({message:'area-scores',...region});
     else {clearTimeout(timeout);progress(100);resolve();}
    }
    if(data.message==='area-scores'){clearTimeout(timeout);progress(100);resolve();}
    if(data.message==='pooled'&&pending){
     if(!validBirdScores(data.pooled,expectedScores)){rejectPending?.(Error('Bird identification returned an invalid result. Try again.'));close();return;}
     clearTimeout(timeout);const done=pending;pending=undefined;rejectPending=undefined;done(data.pooled);
    }
   };
   const modelFiles=files.filter(f=>!f.name.endsWith('.js'));
   worker.postMessage({message:'boot',files:modelFiles},modelFiles.map(f=>f.bytes));
  });
  signal.throwIfAborted();
 }catch(error){close();throw error;}
 return {close,predict(audio){return new Promise((resolve,reject)=>{
  if(closed||pending){reject(Error('Bird identification is unavailable.'));return;}
  pending=resolve;rejectPending=reject;timeout=setTimeout(()=>{reject(Error('Bird identification is too slow on this device. Try again.'));pending=undefined;rejectPending=undefined;close();},BIRD_INFERENCE_TIMEOUT_MS);
  worker.postMessage({message:'predict',pcmAudio:audio,sensitivity:1,overlapSec:0},[audio.buffer]);
 });}};
}
